import { z } from 'zod'
import type { CalculatorDefinition } from '@/types/calculator'
import { positiveMoney } from '@/calculators/shared/schema'
import { calculateBond } from './calculation'
import { content, faqs } from './content'
import type { BondInputs, BondResult } from './types'

const schema = z.object({
  propertyPrice: positiveMoney('Enter a property price').refine((v) => v > 0, {
    message: 'Property price must be greater than 0',
  }),
  deposit: positiveMoney(),
  interestRate: z.number().min(0).max(1),
  termYears: z.number().int().min(1).max(40),
  monthlyIncome: positiveMoney(),
  monthlyDebt: positiveMoney(),
  maxDebtToIncomeRatio: z.number().min(0).max(1),
})

const defaults: BondInputs = {
  propertyPrice: 1_500_000,
  deposit: 150_000,
  interestRate: 0.115,
  termYears: 20,
  monthlyIncome: 50_000,
  monthlyDebt: 5_000,
  maxDebtToIncomeRatio: 0.3,
}

export const bondDefinition: CalculatorDefinition<BondInputs, BondResult> = {
  id: 'bond',
  slug: 'bond-calculator',
  category: 'home',
  title: 'Bond Affordability & Repayment Calculator',
  shortTitle: 'Bond Calculator',
  description:
    'Estimate monthly bond repayments and a simple affordability indicator for South African home finance. Not a lender approval.',
  inputs: [
    {
      name: 'propertyPrice',
      label: 'Property price',
      type: 'money',
      required: true,
      unit: 'R',
      placeholder: 'e.g. 1 500 000',
    },
    {
      name: 'deposit',
      label: 'Deposit',
      type: 'money',
      required: false,
      unit: 'R',
      placeholder: '0',
    },
    {
      name: 'interestRate',
      label: 'Interest rate',
      type: 'percentage',
      required: true,
      unit: '%',
      min: 0,
      max: 1,
      step: 0.001,
      defaultValue: 0.115,
    },
    {
      name: 'termYears',
      label: 'Bond term',
      type: 'number',
      required: true,
      unit: 'years',
      min: 1,
      max: 40,
      step: 1,
      defaultValue: 20,
    },
    {
      name: 'monthlyIncome',
      label: 'Monthly household income (before tax)',
      type: 'money',
      required: false,
      unit: 'R',
      placeholder: 'e.g. 50 000',
    },
    {
      name: 'monthlyDebt',
      label: 'Existing monthly debt repayments',
      type: 'money',
      required: false,
      unit: 'R',
      placeholder: '0',
    },
    {
      name: 'maxDebtToIncomeRatio',
      label: 'Max debt-to-income ratio',
      type: 'percentage',
      required: true,
      unit: '%',
      min: 0,
      max: 1,
      step: 0.01,
      defaultValue: 0.3,
      hint: 'Your own affordability rule of thumb',
    },
  ],
  defaults,
  schema,
  calculate: calculateBond,
  resultSections: [
    {
      id: 'monthlyRepayment',
      label: 'Monthly repayment',
      format: 'currency',
      value: (r: BondResult) => r.monthlyRepayment,
      highlighted: true,
    },
    {
      id: 'loanAmount',
      label: 'Loan amount',
      format: 'currency',
      value: (r: BondResult) => r.loanAmount,
      highlighted: true,
    },
    {
      id: 'affordable',
      label: 'Affordability signal',
      format: 'plain',
      value: (r: BondResult) => r.affordable.toUpperCase(),
      highlighted: true,
    },
    {
      id: 'totalInterest',
      label: 'Total interest over term',
      format: 'currency',
      value: (r: BondResult) => r.totalInterest,
    },
    {
      id: 'totalRepayment',
      label: 'Total repayment',
      format: 'currency',
      value: (r: BondResult) => r.totalRepayment,
    },
    {
      id: 'depositPercent',
      label: 'Deposit percentage',
      format: 'percent',
      value: (r: BondResult) => r.depositPercent,
    },
    {
      id: 'monthlyAffordabilityRatio',
      label: 'Monthly debt-to-income',
      format: 'percent',
      value: (r: BondResult) => r.monthlyAffordabilityRatio,
    },
  ],
  assumptions: [
    {
      name: 'Standard amortising bond formula',
      reviewedAt: '2026-09-11',
      effectiveFrom: '2026-09-01',
      notes:
        'Monthly repayment assumes a fixed interest rate for the full term and no initiation, admin or insurance fees. Interest is compounded monthly.',
    },
  ],
  related: ['transfer-cost-calculator', 'rental-yield-calculator', 'salary-calculator'],
  seo: {
    title: 'Bond Repayment Calculator South Africa',
    description:
      'Estimate your monthly bond repayment, total interest and affordability with this bond repayment and home loan calculator for South Africa.',
    keywords: ['bond calculator', 'bond repayment', 'home loan South Africa', 'affordability'],
  },
  content,
  faqs,
}
